import { memo, useMemo, useCallback, useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Button,
  Divider,
  Collapse,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PolicyIcon from "@mui/icons-material/Policy";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SendIcon from "@mui/icons-material/Send";
import GroupIcon from "@mui/icons-material/Group";
import HowToVoteIcon from "@mui/icons-material/HowToVote";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import SecurityIcon from "@mui/icons-material/Security";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { usePendingActions } from "../context/pendingActionsContext";
import { PendingAction, PendingActionType } from "../types/pendingActions";

interface PendingActionsDrawerProps {
  open: boolean;
  onClose: () => void;
}

const getIconForActionType = (type: PendingActionType) => {
  const icons: Record<PendingActionType, React.ReactNode> = {
    policy: <PolicyIcon />,
    user: <PersonAddIcon />,
    transaction: <SendIcon />,
    group: <GroupIcon />,
    vote: <HowToVoteIcon />,
    account_setup: <AccountCircleIcon />,
    helper: <SecurityIcon />,
  };
  return icons[type] || <PolicyIcon />;
};

// Truncate signature to show first 8 and last 4 characters
const truncateSignature = (sig: string): string => {
  if (!sig || sig.length <= 14) return sig;
  return `${sig.slice(0, 8)}...${sig.slice(-4)}`;
};

const getActionSummaryText = (action: PendingAction): string => {
  const { actionType, summary, isDeleted } = action;

  switch (actionType) {
    case "transaction":
      return summary.amount && summary.asset
        ? `Send ${summary.amount} ${summary.asset}`
        : "Send transaction";
    case "policy":
      return isDeleted
        ? `Remove policy: ${summary.policyName}`
        : `Add policy: ${summary.policyName}`;
    case "user":
      return isDeleted ? `Remove user: ${summary.userName}` : `Add user: ${summary.userName}`;
    case "group":
      return isDeleted
        ? `Remove group: ${summary.groupName}`
        : `Create group: ${summary.groupName}`;
    case "vote":
      return summary.voteChoice ? `Vote: ${summary.voteChoice}` : "Submit vote";
    case "account_setup":
      return summary.includesSafe ? "Setting up your safe" : "Setting up profile";
    case "helper":
      if (summary.helperAction === "add") {
        return summary.helperName ? `Add helper: ${summary.helperName}` : "Add helper";
      } else if (summary.helperAction === "remove") {
        return "Remove helper";
      } else {
        return "Update helpers";
      }
    default:
      return "Pending action";
  }
};

// Individual expandable item component
const PendingActionItem: React.FC<{ action: PendingAction }> = ({ action }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <ListItem
      sx={{
        borderRadius: 1,
        mb: 1,
        bgcolor: "action.hover",
        flexDirection: "column",
        alignItems: "flex-start",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
        }}
      >
        <ListItemIcon sx={{ minWidth: 40 }}>{getIconForActionType(action.actionType)}</ListItemIcon>
        <ListItemText
          primary={getActionSummaryText(action)}
          secondary={formatDistanceToNow(action.timestamp, {
            addSuffix: true,
          })}
          primaryTypographyProps={{
            variant: "body2",
            fontWeight: 500,
          }}
          secondaryTypographyProps={{
            variant: "caption",
          }}
        />
        <IconButton size="small" onClick={() => setExpanded(!expanded)}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>
      <Box sx={{ mt: 1, ml: 5, display: "flex", alignItems: "center", gap: 1 }}>
        <Chip label="Pending" size="small" color="warning" variant="outlined" />
        {action.actionType === "vote" && action.summary.voteChoice && (
          <Chip
            label={action.summary.voteChoice}
            size="small"
            color={action.summary.voteChoice === "YES" ? "success" : "error"}
            sx={{ fontSize: "0.65rem", height: 20 }}
          />
        )}
      </Box>

      {/* Expandable Details */}
      <Collapse in={expanded} sx={{ width: "100%", ml: 5, mt: 1 }}>
        <Box sx={{ pt: 1, borderTop: 1, borderColor: "divider" }}>
          {/* Description */}
          {action.summary.description && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              {action.summary.description}
            </Typography>
          )}

          {/* Signature */}
          <Box sx={{ mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary" component="span">
              Sig:{" "}
            </Typography>
            <Tooltip title={action.signature} placement="top">
              <Typography
                variant="caption"
                component="span"
                sx={{ fontFamily: "monospace", cursor: "help" }}
              >
                {truncateSignature(action.signature)}
              </Typography>
            </Tooltip>
          </Box>

          {/* Target action signature for votes */}
          {action.actionType === "vote" && action.summary.targetActionSignature && (
            <Box sx={{ mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary" component="span">
                Target:{" "}
              </Typography>
              <Tooltip title={action.summary.targetActionSignature} placement="top">
                <Typography
                  variant="caption"
                  component="span"
                  sx={{ fontFamily: "monospace", cursor: "help" }}
                >
                  {truncateSignature(action.summary.targetActionSignature)}
                </Typography>
              </Tooltip>
            </Box>
          )}

          {/* Helper action */}
          {action.actionType === "helper" && action.summary.helperAction && (
            <Box sx={{ mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary" component="span">
                Action:{" "}
              </Typography>
              <Chip
                label={action.summary.helperAction}
                size="small"
                color={
                  action.summary.helperAction === "add"
                    ? "success"
                    : action.summary.helperAction === "remove"
                      ? "error"
                      : "info"
                }
                sx={{ fontSize: "0.6rem", height: 16 }}
              />
            </Box>
          )}

          {/* User address */}
          {action.actionType === "user" && action.summary.userAddress && (
            <Box sx={{ mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary" component="span">
                Address:{" "}
              </Typography>
              <Tooltip title={action.summary.userAddress} placement="top">
                <Typography
                  variant="caption"
                  component="span"
                  sx={{ fontFamily: "monospace", cursor: "help" }}
                >
                  {truncateSignature(action.summary.userAddress)}
                </Typography>
              </Tooltip>
            </Box>
          )}

          {/* Destination */}
          <Box>
            <Typography variant="caption" color="text.secondary" component="span">
              Dest:{" "}
            </Typography>
            <Tooltip title={action.destinationAddress} placement="top">
              <Typography
                variant="caption"
                component="span"
                sx={{ fontFamily: "monospace", cursor: "help" }}
              >
                {truncateSignature(action.destinationAddress)}
              </Typography>
            </Tooltip>
          </Box>
        </Box>
      </Collapse>
    </ListItem>
  );
};

const PendingActionsDrawer: React.FC<PendingActionsDrawerProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { pendingActions } = usePendingActions();

  // Filter pending actions (only show pending status)
  const pendingItems = useMemo(
    () => pendingActions.filter((a) => a.status === "pending"),
    [pendingActions]
  );

  const handleViewActionsCenter = useCallback(() => {
    onClose();
    navigate("/actions");
  }, [navigate, onClose]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: 380, maxWidth: "100%" },
      }}
    >
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6">Pending Actions ({pendingItems.length})</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {pendingItems.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
            No pending actions
          </Typography>
        ) : (
          <List disablePadding sx={{ maxHeight: "calc(100vh - 200px)", overflow: "auto" }}>
            {pendingItems.map((action) => (
              <PendingActionItem key={action.id} action={action} />
            ))}
          </List>
        )}

        <Box sx={{ mt: 3 }}>
          <Button fullWidth variant="outlined" onClick={handleViewActionsCenter}>
            View Actions Center
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default memo(PendingActionsDrawer);
